import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Megaphone } from "lucide-react";
import { bodyOf, fromPrice, inr, media } from "@/lib/format";
import { useKiosk } from "./KioskContext";
import { OfferCard } from "./OfferCard";
import { HeroCampaign } from "./HeroCampaign";
import { playable } from "./Attract";

const PT = ["all", "ice", "ev"];

const filterModels = (models, cat, pt) =>
  models.filter((m) => {
    const inCat = cat === "ev" ? m.powertrain === "EV" : bodyOf(m) === (cat === "moto" ? "Motorcycle" : "Scooter");
    const inPt = pt === "all" || m.powertrain.toLowerCase() === pt;
    return inCat && inPt;
  });

const PowertrainFilter = ({ pt, setPt, cat, t }) => (
  <div className="flex rounded-full bg-[#F5F5F7] p-1" data-testid="powertrain-filter">
    {PT.map((p) => {
      const disabled = (cat === "ev" && p === "ice") || (cat === "moto" && p === "ev");
      return (
        <button key={p} disabled={disabled} data-testid={`pt-${p}`} onClick={() => setPt(p)}
          className={`min-h-[44px] min-w-[64px] rounded-full px-4 text-[15px] font-medium transition-colors disabled:opacity-30 ${pt === p ? "bg-white shadow-sm text-[#1D1D1F]" : "text-[#6E6E73]"}`}>
          {t(p)}
        </button>
      );
    })}
  </div>
);

const Rail = ({ slides, idx, setIdx, tx }) => (
  <div className="flex justify-start gap-3 overflow-x-auto no-scrollbar px-6 lg:justify-center" data-testid="model-rail">
    {slides.map(({ m, c }, i) => (
      <button key={c ? c.id : m.slug} data-testid={c ? `rail-campaign-${c.id}` : `rail-${m.slug}`} onClick={() => setIdx(i)}
        className={`flex min-h-[64px] shrink-0 items-center gap-3 rounded-full pl-2 pr-5 transition-colors ${i === idx ? "bg-[#1D1D1F] text-white" : "bg-[#F5F5F7] text-[#1D1D1F]"}`}>
        {c ? <span className="grid h-12 w-12 place-items-center rounded-full bg-[#0B8577] text-white"><Megaphone size={18} /></span> : <img src={media(m.hero_image)} alt="" className="h-12 w-16 object-contain" />}
        <span className="max-w-[200px] truncate text-[15px] font-semibold whitespace-nowrap">{c ? tx(c.title) : m.short}</span>
      </button>
    ))}
  </div>
);

export default function KioskHome() {
  const { data, t, tx, cat: catLabel, logPlay, online } = useKiosk();
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "moto";
  const [pt, setPt] = useState("all");
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const list = useMemo(() => filterModels(data.models, cat, pt), [data.models, cat, pt]);
  useEffect(() => { setIdx(0); }, [cat, pt]);
  useEffect(() => { if ((cat === "ev" && pt === "ice") || (cat === "moto" && pt === "ev")) setPt("all"); }, [cat, pt]);
  const slides = useMemo(() => [...playable(data.offers, "hero", online).map((c) => ({ c })), ...list.map((m) => ({ m }))], [data.offers, online, list]);
  const s = slides[Math.min(idx, slides.length - 1)];
  const m = s?.m;
  useEffect(() => {
    if (s?.m) logPlay("model", s.m.slug, s.m.name);
    if (s?.c) logPlay("campaign", s.c.id, s.c.title?.en || "");
  }, [s, logPlay]);
  const go = (d) => { if (!slides.length) return; setDir(d); setIdx((i) => (i + d + slides.length) % slides.length); };
  const pick = (i) => { setDir(i > idx ? 1 : -1); setIdx(i); };
  if (!s) return <div className="p-10 text-center text-[#6E6E73]" data-testid="home-empty"><button className="btn-pill btn-ghost" onClick={() => setParams({ cat: "moto" })}>{t("motorcycles")}</button></div>;
  const price = m ? fromPrice(m) : 0;

  return (
    <main className="relative mx-auto flex h-[calc(100dvh-128px)] min-h-[620px] max-w-[1280px] flex-col xl:h-[calc(100dvh-76px)] pk:!h-[calc(100dvh-128px)]" data-testid="kiosk-home">
      <div className="z-10 flex justify-center pt-2 lg:absolute lg:right-10 lg:top-3 lg:pt-0 pk:!static pk:!pt-2"><PowertrainFilter pt={pt} setPt={setPt} cat={cat} t={t} /></div>
      <AnimatePresence mode="wait" custom={dir}>
        {s.c ? <HeroCampaign key={s.c.id} c={s.c} dir={dir} go={go} /> : <motion.section key={m.slug} custom={dir} initial={{ opacity: 0, x: 40 * dir }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 * dir }}
          transition={{ duration: 0.45, ease: [0.2, 0.7, 0.2, 1] }} className="flex min-h-0 flex-1 flex-col items-center pt-6 text-center">
          <p className="text-[#087568] text-[17px] font-semibold" data-testid="hero-segment">{catLabel(m.category)} · {m.powertrain === "EV" ? t("ev") : t("ice")}</p>
          <h1 className="font-display mt-1 text-5xl sm:text-6xl lg:text-[92px] pk:!text-[11vw] font-extrabold leading-[0.98] tracking-[-0.045em]" data-testid="hero-model-name">{m.short}</h1>
          <p className="mt-3 text-lg lg:text-[22px] pk:!text-[3vw] text-[#6E6E73]" data-testid="hero-tagline">{tx(m.tagline)}</p>
          <p className="mt-2 text-[15px] text-[#1D1D1F]" data-testid="hero-price">
            {t("from")} <span className="font-semibold tnum">{inr(price)}</span> · {t("exShowroom")} <span className="text-[#6E6E73]">· {t("indicative")}</span>
          </p>
          <div className="mt-5 flex gap-3">
            <Link to={`/kiosk/model/${m.slug}`} className="btn-pill btn-ink" data-testid="hero-explore-btn">{t("explore")}</Link>
            <Link to={`/kiosk/book/test_ride?model=${m.slug}`} className="btn-pill btn-teal" data-testid="hero-book-btn">{t("bookTestRide")}</Link>
          </div>
          <motion.div drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.25}
            onDragEnd={(_, info) => { if (info.offset.x < -60) go(1); if (info.offset.x > 60) go(-1); }}
            className="relative mt-2 flex min-h-0 w-full flex-1 items-end justify-center" data-testid="hero-swipe-area">
            <div className="floor-glow pointer-events-none absolute bottom-0 left-1/2 h-24 w-[62%] -translate-x-1/2" />
            <img src={media(m.hero_image)} alt={m.name} draggable={false} className="product-shadow relative max-h-full w-auto max-w-[78%] object-contain pb-3 pk:!w-full pk:!max-w-[94%]" data-testid="hero-image" />
          </motion.div>
        </motion.section>}
      </AnimatePresence>
      <button onClick={() => go(-1)} data-testid="hero-prev" aria-label="Previous" className="absolute left-4 top-[38%] hidden h-14 w-14 place-items-center rounded-full bg-[#F5F5F7] hover:bg-[#ECECEF] md:grid"><ChevronLeft /></button>
      <button onClick={() => go(1)} data-testid="hero-next" aria-label="Next" className="absolute right-4 top-[38%] hidden h-14 w-14 place-items-center rounded-full bg-[#F5F5F7] hover:bg-[#ECECEF] md:grid"><ChevronRight /></button>
      {m && <OfferCard modelSlug={m.slug} />}
      <div className="pb-5 pt-2"><Rail slides={slides} idx={Math.min(idx, slides.length - 1)} setIdx={pick} tx={tx} /></div>
    </main>
  );
}
